import { useCallback, useEffect, useRef, useState } from 'react'
import type { DeskWatchedValues } from 'components/events/EventForm/types'

/**
 * The desk's one trigger for "the curator set or changed the town / start in
 * the desk form" (33.3 D-03b). Shared on purpose: Phase 33.1 (the duplicate
 * re-check, docs/spec-desk-recheck-duplicates.md) imports this hook unchanged
 * for its debounced town/date re-check, so there is never a second watcher.
 *
 * Wiring: pass `onWatchedChange` as `EventFormDeskMode.onWatchedChange`. The
 * form reports its current town and start once on mount and again on every
 * change. `resetKey` is the candidate id: a new key starts over.
 *
 * Semantics:
 * - `baseline`: the first report for the current `resetKey`, applied at
 *   once (the values the candidate opened with). Null until it arrives.
 * - `watched`: the latest applied report. Later reports are debounced by
 *   `debounceMs` (DESK_WATCH_DEBOUNCE_MS = 500): only the last of a burst is
 *   applied.
 * - `townVersion` / `startVersion`: each increments when an applied report's
 *   town (sameTown) / start (sameStart) differs from the previously applied
 *   one, so consumers can key effects or memos on them. A -> B -> A counts
 *   twice. A report identical to `watched` changes nothing.
 * - `townChanged` / `startChanged`: the version is above 0, i.e. the curator
 *   changed it at least once for this candidate (it stays true even when she
 *   changes it back).
 * - `onWatchedChange` is referentially stable for the hook's lifetime.
 *
 * No I/O and no location logic here. The "desk filled it vs she typed it"
 * guard is components/events/EventForm/deskOwnedValue.ts, kept separate.
 * Do not narrow DeskWatchedValues: Phase 33.1 reads `startTime`.
 */

export type { DeskWatchedValues } from 'components/events/EventForm/types'

/** The town/start debounce, ~500 ms per spec-desk-recheck-duplicates decision 5. */
export const DESK_WATCH_DEBOUNCE_MS = 500

type TownValues = Pick<DeskWatchedValues, 'cityId' | 'cityText'>

/** NFKD, accents stripped, lower case, trimmed, whitespace collapsed. */
const normTownText = (text: string): string =>
  text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')

/**
 * The same town: equal cityId when either side has one (a listed town never
 * equals a typed one); when both are null, equal normalised typed text.
 */
export const sameTown = (a: TownValues, b: TownValues): boolean => {
  if (a.cityId !== null || b.cityId !== null) {
    return a.cityId === b.cityId
  }
  return normTownText(a.cityText) === normTownText(b.cityText)
}

/** The same start: both null, or the same instant. */
export const sameStart = (a: Date | null, b: Date | null): boolean => {
  if (a === null || b === null) {
    return a === b
  }
  return a.getTime() === b.getTime()
}

export interface DeskTownChange {
  onWatchedChange: (values: DeskWatchedValues) => void
  baseline: DeskWatchedValues | null
  watched: DeskWatchedValues | null
  townVersion: number
  townChanged: boolean
  startVersion: number
  startChanged: boolean
}

interface WatchState {
  key: number | undefined
  baseline: DeskWatchedValues | null
  watched: DeskWatchedValues | null
  townVersion: number
  startVersion: number
}

const initialState = (key: number | undefined): WatchState => ({
  key,
  baseline: null,
  watched: null,
  townVersion: 0,
  startVersion: 0
})

export const useDeskTownChange = (
  resetKey: number | undefined,
  debounceMs: number = DESK_WATCH_DEBOUNCE_MS
): DeskTownChange => {
  // Read in render on purpose: the form's mount report runs in a child effect,
  // before this component's effects, and must already see the new key.
  const keyRef = useRef(resetKey)
  keyRef.current = resetKey
  const debounceRef = useRef(debounceMs)
  debounceRef.current = debounceMs

  const [state, setState] = useState<WatchState>(() => initialState(resetKey))
  // The applied state, kept in step synchronously so two reports in one tick
  // see each other (React state lags until the next render).
  const appliedRef = useRef<WatchState>(state)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const commit = (next: WatchState) => {
    appliedRef.current = next
    setState(next)
  }

  const onWatchedChange = useCallback((values: DeskWatchedValues) => {
    const key = keyRef.current
    if (appliedRef.current.key !== key) {
      clearTimer()
      appliedRef.current = initialState(key)
    }
    const current = appliedRef.current
    if (current.baseline === null) {
      commit({ ...current, baseline: values, watched: values })
      return
    }
    clearTimer()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      const latest = appliedRef.current
      // A report from an earlier candidate never lands on the next one.
      if (latest.key !== key || keyRef.current !== key || latest.watched === null) return
      const townMoved = !sameTown(latest.watched, values)
      const startMoved = !sameStart(latest.watched.startTime, values.startTime)
      if (!townMoved && !startMoved) return
      commit({
        ...latest,
        watched: values,
        townVersion: latest.townVersion + (townMoved ? 1 : 0),
        startVersion: latest.startVersion + (startMoved ? 1 : 0)
      })
    }, debounceRef.current)
    // Only refs and the stable setState are read: the identity never changes.
  }, [])

  // A new candidate: drop the pending report and start over, unless its first
  // report already arrived (the child's mount effect runs before this one).
  useEffect(() => {
    if (appliedRef.current.key !== resetKey) {
      clearTimer()
      commit(initialState(resetKey))
    }
  }, [resetKey])

  useEffect(() => clearTimer, [])

  // Until the reset effect runs, never show the previous candidate's values.
  const view = state.key === resetKey ? state : initialState(resetKey)

  return {
    onWatchedChange,
    baseline: view.baseline,
    watched: view.watched,
    townVersion: view.townVersion,
    townChanged: view.townVersion > 0,
    startVersion: view.startVersion,
    startChanged: view.startVersion > 0
  }
}
